<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="kb-mapping-modal" @click.stop>

      <!-- Header -->
      <div class="modal-header">
        <div class="modal-header-left">
          <div class="modal-title">
            <span class="modal-title-icon"><i class="fas fa-project-diagram"></i></span>
            <span>KB Mapping</span>
            <span class="modal-site-url">— {{ siteUrl }}</span>
            <span v-if="isPageOrFileScope" class="modal-scope-badge">{{ scopeType === 'file' ? 'File override' : 'Page override' }}</span>
          </div>
          <div class="modal-subtitle">Configure which Genesys Cloud Knowledge Base each article category is ingested into.</div>
        </div>
        <button class="modal-close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div v-if="isLoading" class="loading-state">
          <div class="spinner-container">
            <div class="spinner"></div>
            <span>Loading knowledge bases...</span>
          </div>
        </div>

        <div v-else class="kb-mapping-layout">
          <div class="kb-mapping-list-panel">
            <div class="panel-header">
              <h3>Knowledge Base Mappings</h3>
              <button class="btn-add" @click="openAddMapping" :disabled="availableKnowledgeBases.length === 0">
                <i class="fas fa-plus"></i> Add
              </button>
            </div>

            <div v-if="isInherited" class="inherited-banner">
              <i class="fas fa-info-circle"></i>
              These rules are inherited from the source — this {{ scopeType }} has no rules of its own yet.
              Add, edit, or remove a rule to create a custom set for just this {{ scopeType }} (the source's rules will no longer apply here).
            </div>

            <div v-if="mappings.length === 0" class="empty-state">
              No knowledge base mappings configured yet. Click Add to create one.
            </div>

            <table v-else class="kb-mapping-list-table">
              <thead>
                <tr>
                  <th class="th-number">#</th>
                  <th class="th-kb">Knowledge Base</th>
                  <th class="th-status">Status</th>
                  <th class="th-articles">Articles</th>
                  <th class="th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(mapping, index) in mappings" :key="mapping.id" :class="{ 'row-selected': editingMapping?.id === mapping.id }">
                  <td class="td-number">{{ index + 1 }}</td>
                  <td class="td-kb">
                    <span class="kb-name">{{ getKnowledgeBaseName(mapping.knowledge_base_id) }}</span>
                    <span class="kb-description">Genesys Cloud &middot; {{ getKnowledgeBaseDescription(mapping.knowledge_base_id) }}</span>
                  </td>
                  <td class="td-status">
                    <span class="status-pill" :class="mapping.status">{{ mapping.status === 'active' ? 'Active' : 'Inactive' }}</span>
                  </td>
                  <td class="td-articles">{{ mapping.article_count ?? 0 }}</td>
                  <td class="td-actions">
                    <span v-if="isInherited" class="inherited-tag" title="Inherited from the source — editing or removing it creates a custom rule set for just this scope">Inherited</span>
                    <button class="btn-edit" @click="openEditMapping(mapping)">
                      <i class="fas fa-pencil-alt"></i> Edit
                    </button>
                    <button class="btn-remove" @click="removeMapping(mapping)">
                      <i class="fas fa-trash"></i> Remove
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <Transition name="kb-mapping-panel">
            <div class="kb-mapping-edit-panel" v-if="editingMapping">
              <label class="edit-label">Knowledge Base</label>
              <template v-if="isAdding">
                <select v-model="editingMapping.knowledge_base_id" class="kb-select">
                  <option value="" disabled>Select a Knowledge Base...</option>
                  <option v-for="kb in availableKnowledgeBases" :key="kb.id" :value="kb.id">{{ kb.name }}</option>
                </select>
              </template>
              <template v-else>
                <div class="edit-value">{{ getKnowledgeBaseName(editingMapping.knowledge_base_id) }}</div>
              </template>

              <label class="edit-label">Status</label>
              <select v-model="editingMapping.status" class="status-select">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              <label class="edit-label">Mapping Instructions</label>
              <textarea
                v-model="editingMapping.mapping_instructions"
                class="mapping-instructions-textarea"
                rows="10"
                placeholder="Describe the categories, topics, or keywords that identify articles belonging to this KB..."></textarea>

              <div class="mapping-instructions-help">
                This is used by the ingestor to determine if an article being ingested should be routed to this Knowledge Base.
                Describe the categories, topics, or keywords that identify articles belonging to this KB. The ingestor evaluates
                each article against these instructions before routing.
              </div>

              <div class="edit-panel-actions">
                <button class="btn-save" @click="saveMapping" :disabled="!editingMapping.knowledge_base_id">Save Changes</button>
                <button class="btn-cancel-edit" @click="cancelEdit">Cancel</button>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { flashMessage } from '@/functions'

const props = defineProps({
  siteID:   { type: Number, default: null },
  siteType: { type: String, default: 'site' },  // 'site' | 'directory'
  siteUrl:  { type: String, default: '' },
  pageID:   { type: Number, default: null },     // set when editing a page-level override
  fileID:   { type: Number, default: null },     // set when editing a file-level override
})

const emit = defineEmits(['close'])

// Effective scope this modal operates on: a page/file override if given, else the source #20260702KL
const scopeID = props.pageID || props.fileID || props.siteID
const scopeType = props.pageID ? 'page' : props.fileID ? 'file' : props.siteType
const isPageOrFileScope = !!(props.pageID || props.fileID)

const _instance = getCurrentInstance()

const knowledgeBases = ref([])
const isLoading = ref(true)
const mappings = ref([])
const isInherited = ref(false)

const blankMapping = () => ({
  id: null,
  knowledge_base_id: '',
  status: 'active',
  mapping_instructions: '',
  article_count: 0
})

const editingMapping = ref(null)
const isAdding = ref(false)

const showFlashMessage = (type, message) => {
  const color = type === 'success' ? '#059669' : type === 'info' ? '#3b82f6' : '#dc2626'
  const textColor = '#ffffff'
  try {
    const flash = _instance?.appContext.config.globalProperties.$flashMessage
    if (flash && typeof flashMessage === 'function') {
      flashMessage(flash, color, textColor, message)
    }
  } catch (error) {
    console.warn('FlashMessage error:', error)
  }
}

const availableKnowledgeBases = computed(() => {
  const mappedIDs = new Set(mappings.value.map(m => m.knowledge_base_id))
  return knowledgeBases.value.filter(kb => !mappedIDs.has(kb.id))
})

const getKnowledgeBaseName = (kbID) => {
  const kb = knowledgeBases.value.find(k => k.id === kbID)
  return kb ? kb.name : 'Unknown'
}

const getKnowledgeBaseDescription = (kbID) => {
  const kb = knowledgeBases.value.find(k => k.id === kbID)
  return kb?.description || 'Knowledge Base'
}

const openAddMapping = () => {
  isAdding.value = true
  editingMapping.value = blankMapping()
}

const openEditMapping = (mapping) => {
  isAdding.value = false
  editingMapping.value = { ...mapping }
}

// Copy every currently-inherited rule to this page/file's own scope in one pass, applying  #20260702KL
// an edit (editID/editValues) or an exclusion (excludeID, for removal) along the way — so  #20260702KL
// the page ends up with a complete, independent rule set instead of silently losing the  #20260702KL
// untouched rules it didn't mean to fork away from.  #20260702KL
const forkInheritedRules = async ({ excludeID = null, editID = null, editValues = null } = {}) => {
  const forked = []
  for (const rule of mappings.value) {
    if (rule.id === excludeID) {
      continue
    }
    const useEdit = rule.id === editID && editValues

    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/sites/${scopeID}/kb-mapping-rules?source_type=${scopeType}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        knowledge_base_id: rule.knowledge_base_id,
        knowledge_base_name: getKnowledgeBaseName(rule.knowledge_base_id),
        mapping_instructions: useEdit ? editValues.mapping_instructions : rule.mapping_instructions,
        status: useEdit ? editValues.status : rule.status
      })
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      throw new Error(errorBody.detail || `HTTP ${response.status}`)
    }

    const data = await response.json()
    forked.push({ ...data.rule, article_count: rule.article_count })
  }

  mappings.value = forked
  isInherited.value = false
  return forked
}

const cancelEdit = () => {
  editingMapping.value = null
  isAdding.value = false
}

const saveMapping = async () => {
  if (!editingMapping.value.knowledge_base_id) {
    return
  }

  try {
    if (isAdding.value) {
      // Adding a new category while viewing inherited rules forks the whole set first —  #20260702KL
      // copy the untouched inherited rules over, then add the new one alongside them,  #20260702KL
      // so the page doesn't lose the categories it didn't touch.  #20260702KL
      if (isInherited.value) {
        await forkInheritedRules()
      }

      const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/sites/${scopeID}/kb-mapping-rules?source_type=${scopeType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          knowledge_base_id: editingMapping.value.knowledge_base_id,
          knowledge_base_name: getKnowledgeBaseName(editingMapping.value.knowledge_base_id),
          mapping_instructions: editingMapping.value.mapping_instructions,
          status: editingMapping.value.status
        })
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        throw new Error(errorBody.detail || `HTTP ${response.status}`)
      }

      const data = await response.json()
      mappings.value.push(data.rule)
    } else if (isInherited.value) {
      // Editing an inherited rule forks the whole set, applying this edit to the  #20260702KL
      // matching copy so the other untouched rules aren't lost.  #20260702KL
      await forkInheritedRules({
        editID: editingMapping.value.id,
        editValues: {
          mapping_instructions: editingMapping.value.mapping_instructions,
          status: editingMapping.value.status
        }
      })
    } else {
      const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/kb-mapping-rules/${editingMapping.value.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: editingMapping.value.status,
          mapping_instructions: editingMapping.value.mapping_instructions
        })
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        throw new Error(errorBody.detail || `HTTP ${response.status}`)
      }

      const data = await response.json()
      const index = mappings.value.findIndex(m => m.id === editingMapping.value.id)
      if (index !== -1) {
        mappings.value[index] = { ...mappings.value[index], ...data.rule }
      }
    }

    showFlashMessage('success', 'Knowledge base mapping saved')
    cancelEdit()
  } catch (error) {
    console.error('Failed to save KB mapping rule:', error)
    showFlashMessage('error', `Failed to save knowledge base mapping: ${error.message}`)
  }
}

const removeMapping = async (mapping) => {
  if (!confirm(`Remove the mapping for "${getKnowledgeBaseName(mapping.knowledge_base_id)}"?`)) {
    return
  }

  try {
    if (isInherited.value) {
      // Removing an inherited rule forks the rest of the set to this scope, excluding  #20260702KL
      // just the one being removed — the other untouched rules still apply.  #20260702KL
      await forkInheritedRules({ excludeID: mapping.id })
      if (editingMapping.value?.id === mapping.id) {
        cancelEdit()
      }
      showFlashMessage('success', 'Knowledge base mapping removed')
      return
    }

    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/kb-mapping-rules/${mapping.id}`, {
      method: 'DELETE'
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      throw new Error(errorBody.detail || `HTTP ${response.status}`)
    }

    mappings.value = mappings.value.filter(m => m.id !== mapping.id)
    if (editingMapping.value?.id === mapping.id) {
      cancelEdit()
    }
    showFlashMessage('success', 'Knowledge base mapping removed')

    // This was this page/file's last own rule — close so the next open re-fetches and  #20260702KL
    // correctly shows the source's rules as inherited again, instead of a stale empty list.  #20260702KL
    if (isPageOrFileScope && mappings.value.length === 0) {
      emit('close')
    }
  } catch (error) {
    console.error('Failed to remove KB mapping rule:', error)
    showFlashMessage('error', `Failed to remove knowledge base mapping: ${error.message}`)
  }
}

const loadKnowledgeBases = async () => {
  try {
    const company = localStorage.getItem('companyID') || 'default'
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/knowledge-bases?company=${company}`)
    const data = await response.json()
    if (data.success) {
      knowledgeBases.value = data.knowledge_bases.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      )
    }
  } catch (error) {
    console.error('Failed to load knowledge bases:', error)
    showFlashMessage('error', `Failed to load knowledge bases: ${error.message}`)
  }
}

const loadKbMapping = async () => {
  try {
    const company = localStorage.getItem('companyID') || 'default'
    const response = await fetch(`${process.env.VUE_APP_AI_BASE_URL}/api/sites/${scopeID}/kb-mapping?source_type=${scopeType}&company=${company}`)
    if (response.ok) {
      const data = await response.json()
      mappings.value = data.rules
      isInherited.value = !!data.inherited
    }
  } catch (error) {
    console.error('Failed to load KB mapping rules:', error)
    showFlashMessage('error', `Failed to load knowledge base mapping: ${error.message}`)
  }
}

onMounted(async () => {
  isLoading.value = true
  await Promise.all([loadKnowledgeBases(), loadKbMapping()])
  isLoading.value = false
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kb-mapping-modal {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 1000px;
  max-width: 95vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 22px 14px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.modal-title-icon { color: #2563eb; }

.modal-site-url {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
}

.modal-scope-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: #ede9fe;
  color: #6d28d9;
}

.modal-subtitle {
  margin-top: 3px;
  font-size: 12px;
  color: #6b7280;
}

.modal-close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 16px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.15s;
}
.modal-close-btn:hover { color: #374151; }

.modal-body {
  padding: 18px 22px;
  overflow-y: auto;
}

.loading-state,
.empty-state {
  padding: 40px 0;
  text-align: center;
  color: #94a3b8;
}

.inherited-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 12px;
}

.inherited-tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: #f1f5f9;
  color: #64748b;
  margin-right: 6px;
}

.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: kb-mapping-spin 0.8s linear infinite;
}

@keyframes kb-mapping-spin {
  to { transform: rotate(360deg); }
}

.kb-mapping-layout {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.kb-mapping-list-panel {
  flex: 1 1 55%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.panel-header h3 {
  margin: 0;
  font-size: 15px;
  color: #1e293b;
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 7px 14px;
  font-size: 13px;
  cursor: pointer;
}

.btn-add:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-add:disabled {
  background: #93c5fd;
  cursor: not-allowed;
}

.kb-mapping-list-table {
  width: 100%;
  border-collapse: collapse;
}

.kb-mapping-list-table th {
  text-align: left;
  padding: 8px 10px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  border-bottom: 2px solid #e2e8f0;
}

.kb-mapping-list-table td {
  padding: 10px;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
}

.kb-mapping-list-table tr.row-selected {
  background: #eff6ff;
}

.td-kb {
  display: flex;
  flex-direction: column;
}

.kb-name {
  color: #2563eb;
  font-weight: 600;
}

.kb-description {
  color: #94a3b8;
  font-size: 11px;
}

.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

.status-pill.active {
  background: #dcfce7;
  color: #16a34a;
}

.status-pill.inactive {
  background: #f1f5f9;
  color: #64748b;
}

.td-actions {
  white-space: nowrap;
}

.btn-edit,
.btn-remove {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 6px;
  background: white;
}

.btn-edit {
  border: 1px solid #93c5fd;
  color: #2563eb;
}

.btn-edit:hover {
  background: #eff6ff;
}

.btn-remove {
  border: 1px solid #fca5a5;
  color: #dc2626;
}

.btn-remove:hover {
  background: #fef2f2;
}

.kb-mapping-edit-panel {
  flex: 1 1 45%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.kb-mapping-panel-enter-active,
.kb-mapping-panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.kb-mapping-panel-enter-from,
.kb-mapping-panel-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

.edit-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #64748b;
  margin: 12px 0 6px;
}

.edit-label:first-child {
  margin-top: 0;
}

.edit-value {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.kb-select,
.status-select {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
}

.mapping-instructions-textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
}

.mapping-instructions-textarea:focus,
.kb-select:focus,
.status-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.mapping-instructions-help {
  margin-top: 10px;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
}

.edit-panel-actions {
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-save {
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 18px;
  font-size: 13px;
  cursor: pointer;
}

.btn-save:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-save:disabled {
  background: #93c5fd;
  cursor: not-allowed;
}

.btn-cancel-edit {
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 18px;
  font-size: 13px;
  cursor: pointer;
  color: #334155;
}

.btn-cancel-edit:hover {
  background: #f1f5f9;
}
</style>
